/**
 * Risk Assessment Detail — Phase 4 (draft + read-only view).
 *
 * Loads a full RA (header + sections + rows + signatures + updates_available
 * flag per section). Edits in draft mode save per-action (per-row on blur,
 * per-add/delete immediately) so the UI is always consistent with the server.
 *
 * Signed-off RAs are read-only — the Revise + Sign-off actions land in Phase 6.
 * Pull-latest UI for section "↑ update available" badges lands in Phase 5;
 * the flag is already computed server-side so this page just shows the badge.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { apiGet, apiPut, apiPost, apiDelete } from '../utils/api';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import remountOnRouteParams from '../utils/remountOnRouteParams';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import { BTN, BTN_XS } from '../constants/buttons';
import { computeHrn } from '../constants/riskAssessment';
import HrnInput from '../components/HrnInput';
import HazardAutocomplete from '../components/HazardAutocomplete';
import RiskReductionBar from '../components/RiskReductionBar';
import OverallRiskFooter from '../components/OverallRiskFooter';
import { computeOverallStats } from '../utils/raStats';
import ControlsByStage from '../components/ControlsByStage';
import PpePicker from '../components/PpePicker';
import RegsSelector from '../components/RegsSelector';
import SiteNoticesSelector from '../components/SiteNoticesSelector';
import RaReviewSection from '../components/RaReviewSection';
import CollapsibleCard from '../components/CollapsibleCard';
import { PRE_REVIEW_QUESTIONS, POST_REVIEW_QUESTIONS, areAnswersComplete } from '../constants/raReviewQuestions';
import RiskAssessmentSignOffModal from '../components/RiskAssessmentSignOffModal';
import { buildRiskAssessmentDoc } from '../utils/riskAssessmentPdf';
import SharePdfButton from '../components/SharePdfButton';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import ActivityField from '../components/ActivityField';
import { formatDateTime } from '../utils/dateFormat';
import { parseActivity } from '../utils/activityFormat';

// Small inline green-tick badge surfaced in the CollapsibleCard headerExtra
// slot when a review section's questions are all answered. Static SVG so it
// renders identically in light + dark mode.
function CompletedTick() {
  return (
    <span
      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500 text-white shrink-0"
      title="All questions answered"
      aria-label="All questions answered"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

// One instance per RA id: a version tab or Revise changes only `:id`, and the old
// RA's edits stayed live under the new id until the refetch landed (R14 review).
export default remountOnRouteParams(RiskAssessmentDetail, (params) => String(params.id));

function RiskAssessmentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Surface missing-template-block warnings passed in via navigation state
  // from RiskAssessmentBuilder. One-shot: we consume the state and clear it
  // so a manual refresh doesn't keep showing the banner.
  const [missingBlocksWarning, setMissingBlocksWarning] = useState(() => {
    const passed = location.state?.missingBlocks;
    return Array.isArray(passed) && passed.length > 0 ? passed : null;
  });
  useEffect(() => {
    if (!missingBlocksWarning) return;
    // Clear navigation state so a back-then-forward doesn't replay the banner.
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { user } = useAuth();
  const { isAdmin, capabilities, capabilitiesFor } = useDepartment();
  // Dual-scope save model (2026-05-23):
  //   - canSaveToWorkspace: any logged-in user can save into their own My
  //     Space personal library. Server gate honours target_production_id.
  //   - canSaveToDept: only library curators can save into the department-
  //     shared library; admin handled via impersonation. Mirrors the server
  //     library gate requireDepartmentLibraryAuthor, which resolves
  //     `canAuthorRiskAssessments` in the ACTIVE department (tranche 3
  //     increment 5) — was the unscoped `isBuyer || activeRole === 'supervisor'`.
  const canSaveToWorkspace = !!user?.id && !isAdmin;
  const canSaveToDept = !isAdmin && capabilities.canAuthorRiskAssessments === true;
  // Legacy shorthand kept for components that haven't been migrated yet.
  const canSaveToLibrary = canSaveToWorkspace || canSaveToDept;
  // Caller's My Space production id — set lazily from /api/users/me on first
  // need; only required for personal-scope writes.
  const [myPersonalProductionId, setMyPersonalProductionId] = useState(null);
  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    apiGet('/api/users/me').then(async (res) => {
      if (cancelled) return;
      if (res.ok) {
        const data = await res.json().catch(() => null);
        if (data?.personal_space_production_id) {
          setMyPersonalProductionId(data.personal_space_production_id);
        }
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]);

  const [ra, setRa] = useState(null);
  // Pristine snapshot of the last-fetched RA, used by Cancel to revert local
  // edits. Resets on every refresh() success.
  const [pristine, setPristine] = useState(null);
  // Explicit dirty flags drive the bottom-bar pill + button gating. We don't
  // deep-compare ra vs pristine every render — flipping flags on touch is
  // cheap and unambiguous.
  const [dirtyHeader, setDirtyHeader] = useState(false);
  const [dirtyRowIds, setDirtyRowIds] = useState(() => new Set());
  // Section deletions queued for the next Save. Removed locally on click;
  // DELETE only fires on handleSaveAll so the user can review + Cancel.
  const [removedSectionIds, setRemovedSectionIds] = useState(() => new Set());
  // Monotonic counter for temp ids assigned to newly-added (not-yet-saved)
  // sections + their auto-created rows. addBlankSection bumps it.
  const pendingIdCounterRef = useRef(0);
  // COMMITTED MARKER: a pending section/row is "still owed" iff its id is
  // still the local `tmp-section-N` / `tmp-row-N` STRING `addBlankSection`
  // mints it with, not the `_pendingCreate` flag (kept only as a legacy
  // convenience, see handleSaveAll). The id-shape test is the SAME one
  // `removeSection`, the row-PUT loop and `applyFromLibrary` already use
  // throughout this file, so a section that has reached the server (real
  // numeric id swapped in by handleSaveAll, per-section, the instant its
  // POST resolves) is correctly excluded here even if a LATER section in
  // the same Save attempt fails.
  const hasPendingCreate = (ra?.sections || []).some(
    (s) => typeof s.id === 'string' || (s.rows || []).some((r) => typeof r.id === 'string')
  );
  const isDirty = dirtyHeader || dirtyRowIds.size > 0 || removedSectionIds.size > 0 || hasPendingCreate;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Save-specific error, kept SEPARATE from `error` above — deliberately,
  // found while fixing the duplicate-hazard bug (2026-08-29). `error` feeds
  // the top-level `if (error) return <ErrorMessage .../>` early return a few
  // lines below, which replaces the ENTIRE page (including the Save button
  // and every unsaved edit) with a bare error box. That is the right
  // behaviour for "the RA failed to LOAD" (refresh()'s own failure path,
  // which still uses `error`) and the wrong one for "a Save attempt failed"
  // — a save failure must leave the form, and the Save button, on screen so
  // the user can retry (requirement: a genuinely-failed section CAN be
  // retried) and see what did/didn't land (requirement: no blanket
  // failure). Reusing `error` for both was a real, separate defect this
  // task's fix would otherwise have been unusable behind.
  const [saveError, setSaveError] = useState(null);
  const [busyAction, setBusyAction] = useState(false);
  const [showSignOff, setShowSignOff] = useState(false);
  const [confirmRevise, setConfirmRevise] = useState(false);
  // Success toast after a Save-as-template POST. The capture happens inside
  // the unified Sign-off / Save modal now; we keep the banner here because
  // it's a page-level toast that persists after the modal closes.
  // Shape: { name, id, skipped }. Auto-dismisses after 5s.
  const [savedTplBanner, setSavedTplBanner] = useState(null);

  // Auto-dismiss the saved-template banner after 5 seconds.
  useEffect(() => {
    if (!savedTplBanner) return undefined;
    const t = setTimeout(() => setSavedTplBanner(null), 5000);
    return () => clearTimeout(t);
  }, [savedTplBanner]);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await apiGet(`/api/risk-assessments/${id}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to load RA');
      }
      const data = await res.json();
      setRa(data);
      setPristine(data);
      setDirtyHeader(false);
      setDirtyRowIds(new Set());
      setRemovedSectionIds(new Set());
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Warn before leaving with unsaved edits — every in-app navigation (sidebar,
  // "← Risk Assessments", the version tabs), Back, reload and tab close. Was a
  // bare `beforeunload` (R14, 2026-09-15; see `hooks/useUnsavedChangesGuard.js`).
  // Revise and Delete call `allowNextNavigation()`: the server has already
  // acted, so asking "leave without saving?" there would strand the user on
  // a version that no longer matches what they did.
  const leaveGuard = useUnsavedChangesGuard({
    when: isDirty,
    message: "Your changes to this risk assessment haven't been saved. If you leave, they will be lost.",
  });

  // Unique "Who is affected" values seen on any row of this RA, sorted
  // alphabetically. Used to populate a datalist on the Who-affected input
  // so users don't have to retype the same audience across rows. MUST sit
  // above the early returns below — React hooks rule.
  const whoAffectedSuggestions = useMemo(() => {
    const set = new Set();
    for (const s of (ra?.sections || [])) {
      for (const r of (s.rows || [])) {
        const v = (r.who_affected || '').trim();
        if (v) set.add(v);
      }
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [ra]);

  if (loading) return <div className="p-8"><LoadingSpinner /></div>;
  if (error)   return <div className="p-4"><ErrorMessage error={error} /></div>;
  if (!ra)     return <div className="p-4 text-sm">Not found.</div>;

  const isDraft = ra.status === 'draft';
  // Editor gate — mirrors the server (loadEditableRA / sign-off / revise etc.):
  // canAuthorRiskAssessments resolved in the RA's OWN department, OR the creator
  // by user_id. Was the unscoped `isBuyer` (tranche 3 increment 5).
  const canEdit = isDraft && (isAdmin || capabilitiesFor(ra.department_id).canAuthorRiskAssessments === true || (user && ra.created_by === user.id));
  // Sign-off + revise require the same authorship gate (creator or oversight).
  const isAuthor = isAdmin || capabilitiesFor(ra.department_id).canAuthorRiskAssessments === true || (user && ra.created_by === user.id);
  const isArchived = !!ra.archived_at;
  // Whether a revision already exists (you can't revise twice).
  const hasNewerVersion = !!ra.next_version;

  const handleSigned = async () => {
    setShowSignOff(false);
    await refresh();
  };

  // Create one or two templates from the current RA (dept lib + personal
  // workspace are independent POSTs). Returns true on success so callers
  // (the unified Sign-off / Save modal) can chain it after a save. Throws
  // are caught and surfaced via the returned boolean — also as a thrown
  // Error so the modal's catch block can show the message locally.
  const saveAsTemplate = async (name, targets) => {
    const trimmed = (name || '').trim();
    if (!trimmed) throw new Error('Template name is required');
    const wantWorkspace = targets?.toWorkspace && canSaveToWorkspace && myPersonalProductionId;
    const wantDept = targets?.toDept && canSaveToDept;
    if (!wantWorkspace && !wantDept) throw new Error('Pick at least one destination');

    const tasks = [];
    if (wantWorkspace) {
      tasks.push(apiPost(`/api/risk-assessments/${ra.id}/create-template`, {
        name: trimmed,
        target_production_id: myPersonalProductionId,
      }));
    }
    if (wantDept) {
      tasks.push(apiPost(`/api/risk-assessments/${ra.id}/create-template`, { name: trimmed }));
    }
    const results = await Promise.all(tasks);
    let lastData = null;
    for (const res of results) {
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error(data?.error || 'Failed to save template');
      lastData = data;
    }
    setSavedTplBanner({
      name: lastData?.name || trimmed,
      id: lastData?.id,
      skipped: Array.isArray(lastData?.skipped_sections) ? lastData.skipped_sections : [],
    });
    return true;
  };

  const handleRevise = async () => {
    setBusyAction(true);
    setError(null);
    try {
      const res = await apiPost(`/api/risk-assessments/${id}/revise`, {});
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to revise');
      }
      const data = await res.json();
      leaveGuard.allowNextNavigation();
      navigate(`/risk-assessments/${data.id}`, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      // CRITICAL: React Router reuses the same component when only :id
      // changes, so state persists across the navigate. Without this reset,
      // busyAction stayed true and the Sign Off / Save buttons on the new
      // draft were silently disabled until the user hard-refreshed.
      setBusyAction(false);
    }
  };

  // Promote an archived version back to "current". Server archives the
  // previously-current sibling and un-archives this one.
  const handleMakeCurrent = async () => {
    if (!window.confirm(`Make this version (v${ra.version}) the current one? The currently-current version will be archived.`)) return;
    setBusyAction(true);
    setError(null);
    try {
      const res = await apiPost(`/api/risk-assessments/${id}/make-current`, {});
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to make current');
      }
      await refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction(false);
    }
  };

  // Soft-delete a non-current version. Blocked server-side when current.
  const handleDeleteVersion = async () => {
    if (!window.confirm(`Delete v${ra.version}? This hides it from the version tabs. You can't undo this from the UI.`)) return;
    setBusyAction(true);
    setError(null);
    try {
      const res = await apiDelete(`/api/risk-assessments/${id}`);
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || 'Failed to delete');
      }
      // Find any non-deleted sibling to navigate to (prefer current).
      const sibling = (ra.version_chain || []).find((v) => v.id !== ra.id);
      leaveGuard.allowNextNavigation();
      if (sibling) {
        navigate(`/risk-assessments/${sibling.id}`, { replace: true });
      } else {
        navigate('/risk-assessments');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyAction(false);
    }
  };

  // ── Header edits (local-only, flushed on Save) ──────────────────
  // The auto-save model was replaced with an explicit Save button so users
  // get a clear commit point for regulated-document changes. Edits live in
  // local state; nothing hits the server until handleSaveAll runs.
  const updateHeaderField = (field, value) => {
    if (!canEdit) return;
    setRa((prev) => ({ ...prev, [field]: value }));
    setDirtyHeader(true);
  };

  // ── Row edits (local-only, flushed on Save) ─────────────────────
  const updateRow = (rowId, patch, sectionId) => {
    if (!canEdit) return;
    setRa((prev) => ({
      ...prev,
      sections: prev.sections.map((s) =>
        s.id !== sectionId ? s : {
          ...s,
          rows: s.rows.map((r) => (r.id === rowId ? { ...r, ...patch, is_customised: 1 } : r)),
        }
      ),
    }));
    markRowDirty(rowId);
  };

  // Mark a row dirty without committing a value — used by text-field onChange
  // handlers so the save-bar pill flips on keystroke instead of on blur.
  const markRowDirty = (rowId) => {
    if (!canEdit) return;
    setDirtyRowIds((prev) => {
      if (prev.has(rowId)) return prev;
      const next = new Set(prev);
      next.add(rowId);
      return next;
    });
  };

  // ── Save all (batch flush of every dirty field) ─────────────────
  const HEADER_FIELDS = [
    'title', 'reference', 'location', 'activity', 'assessor_name',
    'assessment_date', 'review_date', 'ppe_list', 'regs_list', 'notices',
    'pre_review_answers', 'post_review_answers',
  ];
  // Returns `true` on success, `false` if the save failed (callers like the
  // Sign-off modal use this to chain "save then sign-off" actions).
  const handleSaveAll = async () => {
    if (!canEdit || !isDirty) return true;
    setBusyAction(true);
    setSaveError(null);
    // Hazards (pending sections) actually committed to the server THIS
    // call — used only to make a partial-failure message honest, never to
    // decide what to (re-)send: that decision is made structurally below,
    // from the id shape of ra.sections itself (see hasPendingCreate above).
    let sectionsCommittedThisAttempt = 0;
    try {
      // 0. POST any locally-added (pending) sections + their auto-created
      // rows.
      //
      // THE FIX (2026-08-29): this used to derive its worklist from
      // `_pendingCreate`, a flag cleared ONLY by the full-success refresh()
      // at the bottom of this function. So a section that POSTed fine but
      // was followed by a failure — a later section's POST, the header PUT,
      // a row PUT, a section DELETE — stayed marked pending, and the next
      // Save press re-POSTed a hazard already sitting in the database,
      // duplicating it. Fixed by making "still owed" a structural property
      // of the section itself (its id is still a local `tmp-section-N` /
      // `tmp-row-N` STRING) rather than a flag someone has to remember to
      // clear, and by clearing it via `setRa` the INSTANT each POST
      // resolves — not deferred to the end. A section that reaches the
      // server is therefore excluded from every future `sectionsToCreate`
      // computation from that point on, in this attempt or any retry,
      // however many times Save is pressed.
      //
      // 🚨 THIS CLOSES ONE OF TWO DUPLICATE-SOURCING DOORS, NOT BOTH. It
      // closes "the POST succeeded, we received and parsed the response,
      // and a LATER step failed" — the reported bug, and the common case.
      // It does NOT close "the POST succeeded on the server but the
      // response never arrived" (a dropped connection between the server
      // committing the INSERT and the reply reaching this tab): there is no
      // id to swap in, the section is indistinguishable from one that never
      // sent, and a retry re-POSTs it — the same duplicate through a
      // different door. Closing THAT needs the server to recognise a
      // repeated attempt (an Idempotency-Key on this route, the pattern
      // `POST /api/orders` already uses via `useIdempotencyKey`); checked
      // against `server/tests/api/idempotency-key.test.js`'s docblock
      // 2026-08-29 and confirmed `POST /:id/sections` is NOT one of the
      // five HIGH-harm endpoints that middleware currently protects. That
      // is server-side follow-up work, out of scope here.
      const sectionsToCreate = ra.sections.filter(
        (s) => typeof s.id === 'string' || (s.rows || []).some((r) => typeof r.id === 'string')
      );
      for (const ps of sectionsToCreate) {
        let realSectionId = ps.id;
        if (typeof ps.id === 'string') {
          // eslint-disable-next-line no-await-in-loop
          const secRes = await apiPost(`/api/risk-assessments/${id}/sections`, {
            source: 'blank',
            name: ps.name || 'New hazard',
          });
          if (!secRes.ok) {
            const errBody = await secRes.json().catch(() => null);
            throw new Error(`Failed to add hazard "${ps.name || 'New hazard'}": ${errBody?.error || 'unknown error'}`);
          }
          // eslint-disable-next-line no-await-in-loop
          const created = await secRes.json();
          realSectionId = created.id;
          // Commit NOW, not at the end of handleSaveAll — this is the whole
          // fix. Any failure from here on leaves this section correctly
          // marked as already-saved.
          setRa((prev) => ({
            ...prev,
            sections: prev.sections.map((s) => (
              s.id === ps.id ? { ...s, id: realSectionId, _pendingCreate: false } : s
            )),
          }));
        }
        const row = (ps.rows || [])[0];
        if (row && typeof row.id === 'string') {
          // eslint-disable-next-line no-await-in-loop
          const rowRes = await apiPost(`/api/risk-assessments/${id}/sections/${realSectionId}/rows`, {
            hazard: row.hazard || '',
            who_affected: row.who_affected || '',
            controls: row.controls || '',
            notes: row.notes || '',
            lo_init: row.lo_init, fe_init: row.fe_init, dph_init: row.dph_init, np_init: row.np_init,
            lo_res: row.lo_res, fe_res: row.fe_res, dph_res: row.dph_res, np_res: row.np_res,
            ppe_list: row.ppe_list || null,
            regs_list: row.regs_list || null,
          });
          if (!rowRes.ok) {
            const errBody = await rowRes.json().catch(() => null);
            throw new Error(`Failed to save hazard "${ps.name || row.hazard || 'New hazard'}" details: ${errBody?.error || 'unknown error'}`);
          }
          // eslint-disable-next-line no-await-in-loop
          const createdRow = await rowRes.json();
          const committedRowId = row.id;
          setRa((prev) => ({
            ...prev,
            sections: prev.sections.map((s) => (
              s.id === realSectionId
                ? { ...s, rows: s.rows.map((r) => (r.id === committedRowId ? { ...r, id: createdRow.id } : r)) }
                : s
            )),
          }));
        }
        sectionsCommittedThisAttempt += 1;
      }
      // 1. Header — one PUT carries every header + RA-level list field.
      if (dirtyHeader) {
        const body = {};
        for (const f of HEADER_FIELDS) body[f] = ra[f];
        const res = await apiPut(`/api/risk-assessments/${id}`, body);
        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          throw new Error(errBody?.error || 'Failed to save header');
        }
      }
      // 2. One PUT per dirty row. Sequential so an early failure surfaces
      // the offending row without leaving later rows in an inconsistent state.
      // Rows in sections queued for deletion are skipped — the DELETE in
      // step 3 will wipe them anyway. Temp-id rows (newly added in step 0)
      // are also skipped — their content was sent in step 0's POST.
      for (const rowId of dirtyRowIds) {
        if (typeof rowId === 'string') continue;
        let foundRow = null;
        let foundSectionId = null;
        for (const s of ra.sections) {
          const r = (s.rows || []).find((row) => row.id === rowId);
          if (r) { foundRow = r; foundSectionId = s.id; break; }
        }
        if (!foundRow) continue;
        if (removedSectionIds.has(foundSectionId)) continue;
        // eslint-disable-next-line no-await-in-loop
        const res = await apiPut(`/api/risk-assessments/${id}/rows/${rowId}`, foundRow);
        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          throw new Error(errBody?.error || `Failed to save row ${rowId}`);
        }
      }
      // 3. DELETE each queued section. Sequential for the same reason —
      // the first failure surfaces cleanly.
      for (const sectionId of removedSectionIds) {
        // eslint-disable-next-line no-await-in-loop
        const res = await apiDelete(`/api/risk-assessments/${id}/sections/${sectionId}`);
        if (!res.ok) {
          const errBody = await res.json().catch(() => null);
          throw new Error(errBody?.error || `Failed to remove hazard ${sectionId}`);
        }
      }
      // Re-fetch to pick up server-side derived state (next_version,
      // updates_available flags, etc.) — also resets dirty flags + pristine.
      await refresh();
      return true;
    } catch (err) {
      // Partial-outcome vocabulary (matches NewOrder.js's create-then-attach
      // pattern): if any hazard was actually committed to the server this
      // attempt, say so — "the RA was partly saved, here's what's still
      // owed" — rather than a blanket failure that reads as "nothing
      // happened" and invites a resubmit that duplicates what already
      // landed. Nothing typed is discarded either way: ra state still holds
      // every section, committed or not, and a retry only re-sends what
      // hasPendingCreate (id-shape) still marks as owed.
      const savedNote = sectionsCommittedThisAttempt > 0
        ? ` (${sectionsCommittedThisAttempt} hazard${sectionsCommittedThisAttempt === 1 ? '' : 's'} saved this attempt before this happened — nothing typed was lost; press Save again to retry the rest)`
        : '';
      setSaveError(`${err.message}${savedNote}`);
      return false;
    } finally {
      setBusyAction(false);
    }
  };

  // ── Cancel — revert to last-fetched pristine snapshot ───────────
  const handleCancel = () => {
    if (!isDirty) return;
    if (!window.confirm('Discard all unsaved changes since the last save?')) return;
    if (pristine) {
      setRa(pristine);
      setDirtyHeader(false);
      setDirtyRowIds(new Set());
      setRemovedSectionIds(new Set());
    }
  };

  // NOTE: v1.1 dropped per-row add/remove. Sections are now atomic (1 hazard
  // each), so adding a hazard = adding a section (`addBlankSection` below
  // chains the section + row create), and removing a hazard = removing its
  // section (`removeSection` below). The PUT-on-blur edit path still uses
  // `saveRow` above.

  // Deferred — flushes on Save like header + row edits. Removes locally and
  // queues the section id for DELETE in handleSaveAll. Cancel reverts via
  // the pristine snapshot. Matches the batched-save semantics the rest of
  // the editor uses. Pending (temp-id) sections that never made it to the
  // server are just dropped — no DELETE call queued.
  const removeSection = (sectionId) => {
    if (!canEdit) return;
    if (!window.confirm('Remove this hazard? You can still Cancel before saving to undo.')) return;
    setRa((prev) => ({ ...prev, sections: prev.sections.filter((s) => s.id !== sectionId) }));
    if (typeof sectionId === 'number') {
      setRemovedSectionIds((prev) => {
        const next = new Set(prev);
        next.add(sectionId);
        return next;
      });
    }
  };

  // v1.1: sections are atomic (1 hazard each). Adding stages a temp-id
  // section + row in local state; both POSTs fire from handleSaveAll. This
  // matches the deferred-delete model so structural changes (add/remove) are
  // consistently batched and Cancel can undo them.
  //
  // Temp ids are strings ("tmp-section-N", "tmp-row-N") so we can tell them
  // apart from server-assigned numeric ids when iterating in save/delete.
  const addBlankSection = () => {
    if (!canEdit) return;
    pendingIdCounterRef.current += 1;
    const n = pendingIdCounterRef.current;
    const tempSection = {
      id: `tmp-section-${n}`,
      name: 'New hazard',
      _pendingCreate: true,
      rows: [{
        id: `tmp-row-${n}`,
        hazard: '', who_affected: '', controls: '', notes: '',
        lo_init: 1, fe_init: 1, dph_init: 0.1, np_init: 1,
        lo_res: 1, fe_res: 1, dph_res: 0.1, np_res: 1,
      }],
    };
    setRa((prev) => ({ ...prev, sections: [...(prev.sections || []), tempSection] }));
  };

  // Apply a library block selection to a hazard row: fetch the block payload,
  // take its (single) row, and PUT it onto the existing row. Also optimistically
  // sets the section name to the block's name — there is no server endpoint to
  // rename a section, so this is local-only and will revert on a full refresh.
  // We accept that trade-off because the hazard text (the new source of
  // identity for atomic sections) IS persisted by the row PUT.
  const applyFromLibrary = async (sectionId, rowId, block) => {
    if (!canEdit) return;
    setBusyAction(true);
    try {
      await (async () => {
        const detailRes = await apiGet(`/api/risk-assessments/blocks/${block.kind}/${block.id}`);
        if (!detailRes.ok) throw new Error('Failed to load library hazard');
        const detail = await detailRes.json();
        const src = (detail.rows && detail.rows[0]) || {};
        const payload = {
          hazard: src.hazard || block.name || '',
          who_affected: src.who_affected || '',
          controls: src.controls || '',
          notes: src.notes || '',
          lo_init: src.lo_init, fe_init: src.fe_init, dph_init: src.dph_init, np_init: src.np_init,
          lo_res: src.lo_res, fe_res: src.fe_res, dph_res: src.dph_res, np_res: src.np_res,
        };
        // Pending sections (added via "+ Add hazard", still string temp ids)
        // have no server-side row yet — skip the PUT and rely on the local
        // update below. handleSaveAll's pending-section pass will POST the
        // full populated payload once the user saves.
        const isPendingRow = typeof rowId === 'string';
        if (!isPendingRow) {
          const putRes = await apiPut(`/api/risk-assessments/${id}/rows/${rowId}`, payload);
          if (!putRes.ok) throw new Error('Failed to apply library hazard');
        }
        // Optimistic local update (section name + row fields). For pending
        // rows this IS the source of truth until save-all runs.
        setRa((prev) => ({
          ...prev,
          sections: prev.sections.map((s) =>
            s.id !== sectionId
              ? s
              : {
                  ...s,
                  name: block.name || s.name,
                  rows: s.rows.map((r) =>
                    r.id !== rowId ? r : { ...r, ...payload, is_customised: 1 }
                  ),
                }
          ),
        }));
      })();
    } catch (err) {
      console.error('[Detail] apply-from-library failed:', err);
    }
    setBusyAction(false);
  };

  // Save the current row's hazard as a new bespoke block. `targets` is an
  // object with `toWorkspace` and `toDept` booleans (set by the dual-scope
  // checkboxes in HazardAutocomplete). One POST per ticked target; the
  // workspace POST passes target_production_id pointing at the caller's My
  // Space, while the dept POST omits it so the server falls back to the
  // active dept (existing behaviour). `category` (optional) lands in the
  // ra_blocks.category column so the autocomplete dropdown can group it.
  const saveAsLibraryBlock = async (sectionId, rowId, targets = {}, category = null) => {
    if (!canEdit) return;
    const section = ra.sections.find((s) => s.id === sectionId);
    const row = section && section.rows.find((r) => r.id === rowId);
    if (!row || !row.hazard || !row.hazard.trim()) return;
    const wantWorkspace = targets.toWorkspace && canSaveToWorkspace && myPersonalProductionId;
    const wantDept = targets.toDept && canSaveToDept;
    if (!wantWorkspace && !wantDept) return;
    setBusyAction(true);
    const payloadBase = {
      name: row.hazard.trim(),
      description: '',
      category: category || null,
      rows: [{
        hazard: row.hazard, who_affected: row.who_affected,
        controls: row.controls, notes: row.notes,
        lo_init: row.lo_init, fe_init: row.fe_init, dph_init: row.dph_init, np_init: row.np_init,
        lo_res: row.lo_res, fe_res: row.fe_res, dph_res: row.dph_res, np_res: row.np_res,
      }],
    };
    try {
      const tasks = [];
      if (wantWorkspace) {
        tasks.push(apiPost('/api/risk-assessments/blocks', {
          ...payloadBase,
          target_production_id: myPersonalProductionId,
        }));
      }
      if (wantDept) {
        tasks.push(apiPost('/api/risk-assessments/blocks', payloadBase));
      }
      const results = await Promise.all(tasks);
      for (const res of results) {
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error || 'Failed to save hazard to library');
        }
      }
    } catch (err) {
      console.error('[Detail] save-as-library-block failed:', err);
    }
    setBusyAction(false);
  };

  const pullLatest = async (sectionId) => {
    if (!canEdit) return;
    setBusyAction(true);
    try {
      await (async () => {
        const res = await apiPost(`/api/risk-assessments/${id}/sections/${sectionId}/pull-latest`, {});
        if (!res.ok) {
          const body = await res.json().catch(() => null);
          throw new Error(body?.error || 'Failed to pull latest');
        }
        const result = await res.json();
        await refresh();
        if (!result.noop) {
          console.log(`[Detail] pull-latest: +${result.added} new, kept ${result.kept_customised} customised, replaced ${result.replaced}`);
        }
      })();
    } catch (err) {
      setError(err.message);
    }
    setBusyAction(false);
  };

  const overallStats = computeOverallStats(
    (ra.sections || []).flatMap((s) => s.rows || [])
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 max-w-full mx-auto pb-32">
      {leaveGuard.dialog}
      {missingBlocksWarning && (
        <div
          className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/60 text-amber-900 dark:text-amber-200 flex items-start gap-3 flex-wrap"
          role="alert"
          data-testid="ra-detail-missing-blocks-banner"
        >
          <span className="text-sm flex-1 min-w-[200px]">
            <b>One or more hazard templates were missing or have been deleted</b> and were skipped while creating this RA
            {missingBlocksWarning.length > 0 && (
              <>: <i>{missingBlocksWarning.join(', ')}</i></>
            )}
            . The remaining hazards were loaded successfully — you can add the missing ones manually.
          </span>
          <button
            type="button"
            onClick={() => setMissingBlocksWarning(null)}
            className="text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 text-xl leading-none px-1"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      {savedTplBanner && (
        <div
          className="mb-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700/60 text-green-800 dark:text-green-200 flex items-center gap-3 flex-wrap"
          data-testid="ra-detail-save-as-template-banner"
          role="status"
        >
          <span className="text-sm flex-1 min-w-[200px]">
            Template <b>“{savedTplBanner.name}”</b> saved to your library
            {savedTplBanner.skipped.length > 0 && (
              <>
                {' '}
                <span className="text-green-700 dark:text-green-300/90">
                  ({savedTplBanner.skipped.length} section{savedTplBanner.skipped.length === 1 ? '' : 's'} skipped — no source block)
                </span>
              </>
            )}
          </span>
          <Link
            to="/risk-assessments/library"
            className="text-sm font-medium text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 underline"
          >
            View
          </Link>
          <button
            type="button"
            onClick={() => setSavedTplBanner(null)}
            className="text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-100 text-xl leading-none px-1"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}
      <header className="mb-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button
            onClick={() => navigate('/risk-assessments')}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-1"
          >
            ← Risk Assessments
          </button>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-baseline gap-2 flex-wrap">
            <HeaderTitle ra={ra} canEdit={canEdit} onSave={(v) => updateHeaderField('title', v)} onDirty={() => setDirtyHeader(true)} />
            <span className="text-sm font-normal text-gray-400 dark:text-gray-500">v{ra.version}</span>
            {ra.status === 'signed_off' && (
              <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                <span aria-hidden>🔒</span> Signed off
              </span>
            )}
            {isArchived && (
              <span className="text-xs inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                Archived
              </span>
            )}
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
              <span className="mr-1 text-gray-400 dark:text-gray-500">—</span>
              Created by {ra.created_by_name || '?'} on {formatDateTime(ra.created_at)}
              <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>
              <span title="Scored using the Hazard Rating Number method: HRN = LO × FE × DPH × NP, per EN ISO 12100 + ISO/TR 14121-2">
                HRN (Hazard Rating Number) method, per ISO 12100
              </span>
            </span>
          </h1>
          {/* Prev/next text links replaced by full-chain version tab bar
              rendered below the <header> block (see ra-detail-version-tabs).
              Server still returns previous_version / next_version for
              back-compat, but they are no longer rendered here. */}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {canEdit && isDirty && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={busyAction}
              className={BTN.secondary}
              data-testid="ra-detail-cancel-top"
            >
              Cancel
            </button>
          )}
          {isAuthor && isDraft && (
            <button
              type="button"
              onClick={() => setShowSignOff(true)}
              className={BTN.confirm}
              disabled={busyAction}
              data-testid="ra-detail-signoff"
            >
              Save / Sign off
            </button>
          )}
          {isAuthor && ra.status === 'signed_off' && !hasNewerVersion && !ra.archived_at && (
            confirmRevise ? (
              <>
                <span className="text-xs text-amber-600 dark:text-amber-400">Revise → new v{ra.version + 1}?</span>
                <button
                  type="button"
                  onClick={handleRevise}
                  className={BTN.warning}
                  disabled={busyAction}
                  data-testid="ra-detail-revise-confirm"
                >
                  Yes, revise
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRevise(false)}
                  className={BTN.secondary}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmRevise(true)}
                className={BTN.secondary}
                disabled={busyAction}
                data-testid="ra-detail-revise"
              >
                Revise → new version
              </button>
            )
          )}
          <SharePdfButton
            renderPdfMakeDoc={({ activeExtras }) => buildRiskAssessmentDoc(ra, {
              includeSignOnSheet: !!activeExtras.signOnSheet,
              includeReviewQuestions: !!activeExtras.reviewQuestions,
            })}
            extras={[
              { key: 'signOnSheet', label: 'sign-on sheet', filenameSuffix: '-with-signon' },
              { key: 'reviewQuestions', label: 'pre/post review questionnaire', filenameSuffix: '-with-review' },
            ]}
            extrasOptional
            /* base RA always renders; sign-on + review are optional add-ons */
            filename={`RA-${ra.reference || ra.id}-v${ra.version}.pdf`}
            title={ra.title}
            orientation="landscape"
            dropDirection="down"
            className={BTN.secondary}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Download / Print
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </SharePdfButton>
        </div>
      </header>

      {ra.archived_at && isAuthor && (
        <div
          className="mb-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700/60 text-amber-800 dark:text-amber-200 flex items-center gap-3 flex-wrap"
          data-testid="ra-detail-archived-banner"
        >
          <span className="text-sm flex-1 min-w-[200px]">
            <b>You're viewing v{ra.version} (archived)</b> — this isn't the current version of this RA.
            You can make it current, or delete it.
          </span>
          <button
            type="button"
            onClick={handleMakeCurrent}
            disabled={busyAction || isDirty}
            title={isDirty ? 'Save changes first' : undefined}
            className={BTN.confirm}
            data-testid="ra-detail-make-current"
          >
            Make this version current
          </button>
          <button
            type="button"
            onClick={handleDeleteVersion}
            disabled={busyAction || isDirty}
            title={isDirty ? 'Save changes first' : undefined}
            className={BTN['danger-soft']}
            data-testid="ra-detail-delete-version"
          >
            Delete this version
          </button>
        </div>
      )}

      {ra.version_chain && ra.version_chain.length > 1 && (
        <div className="flex gap-1 mb-3 border-b border-gray-200 dark:border-gray-700" data-testid="ra-detail-version-tabs">
          {ra.version_chain.map((v) => {
            const isCurrent = v.id === ra.id;
            return (
              <button
                key={v.id}
                onClick={() => !isCurrent && navigate(`/risk-assessments/${v.id}`)}
                disabled={isCurrent}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                  isCurrent
                    ? 'border-indigo-500 text-indigo-700 dark:text-indigo-300 cursor-default'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600 cursor-pointer'
                }`}
              >
                v{v.version}
                {v.status === 'signed_off' && (
                  <span className="ml-1.5 text-[10px] inline-flex items-center px-1 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" title="Signed off">
                    🔒
                  </span>
                )}
                {v.status === 'draft' && (
                  <span className="ml-1.5 text-[10px] text-gray-400 dark:text-gray-500">draft</span>
                )}
                {v.is_current && !isCurrent && (
                  <span className="ml-1.5 text-[10px] inline-flex items-center px-1 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300" title="The current version">
                    ★
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {error && <ErrorMessage error={error} />}
      {saveError && (
        <div data-testid="ra-detail-save-error">
          <ErrorMessage error={saveError} />
        </div>
      )}

      <CollapsibleCard
        id={`ra-${ra.id}-summary`}
        title="Summary"
        tint="blue"
        number={1}
        headerExtra={
          (parseActivity(ra.activity).name || '').trim() && (ra.assessor_name || '').trim()
            ? <CompletedTick />
            : null
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <ActivityField value={ra.activity} canEdit={canEdit} onSave={(v) => updateHeaderField('activity', v)} onDirty={() => setDirtyHeader(true)} />
          <div className="grid gap-3">
            <HeaderField label="Reference" value={ra.reference} canEdit={canEdit} onSave={(v) => updateHeaderField('reference', v)} onDirty={() => setDirtyHeader(true)} />
            <HeaderField label="Location" value={ra.location} canEdit={canEdit} onSave={(v) => updateHeaderField('location', v)} onDirty={() => setDirtyHeader(true)} />
            <HeaderField label="Assessor" value={ra.assessor_name} canEdit={canEdit} onSave={(v) => updateHeaderField('assessor_name', v)} onDirty={() => setDirtyHeader(true)} />
            <div className="grid grid-cols-2 gap-3">
              <HeaderField label="Assessment date" value={ra.assessment_date} canEdit={canEdit} onSave={(v) => updateHeaderField('assessment_date', v)} onDirty={() => setDirtyHeader(true)} type="date" />
              <HeaderField label="Review date" value={ra.review_date} canEdit={canEdit} onSave={(v) => updateHeaderField('review_date', v)} onDirty={() => setDirtyHeader(true)} type="date" />
            </div>
          </div>
        </div>
        {/* Chunk C.5: PPE is task-level, not per-hazard. One picker for the
            whole RA; auto-merges hazard recommendations as they're added. */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
            PPE required for this task
          </span>
          <PpePicker
            value={ra.ppe_list}
            onChange={(next) => setRa((prev) => ({ ...prev, ppe_list: next }))}
            onCommit={(next) => updateHeaderField('ppe_list', next)}
            disabled={!canEdit}
          />
        </div>
        {/* Chunk F (reframed): site notices — Hot Set, Pyro Active, Wind
            Hold, etc. RA-level callouts; render as amber banner on PDF cover. */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">
            Site notices
          </span>
          <SiteNoticesSelector
            value={ra.notices}
            onChange={(next) => setRa((prev) => ({ ...prev, notices: next }))}
            onCommit={(next) => updateHeaderField('notices', next)}
            disabled={!canEdit}
          />
        </div>
        {/* Chunk D: applicable regulations — task-level, auto-merged from
            each added hazard's recommended regs. */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="mb-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Applicable regulations
            </span>
          </div>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <RegsSelector
                value={ra.regs_list}
                onChange={(next) => setRa((prev) => ({ ...prev, regs_list: next }))}
                onCommit={(next) => updateHeaderField('regs_list', next)}
                disabled={!canEdit}
              />
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={async () => {
                  // eslint-disable-next-line no-alert
                  const ok = window.confirm(
                    'Reset applicable regulations to the auto-derived list from this RA’s hazards?\n\nAny manual additions or removals you’ve made will be lost.'
                  );
                  if (!ok) return;
                  setBusyAction(true);
                  try {
                    const res = await apiPost(`/api/risk-assessments/${id}/regs/reset`);
                    if (!res.ok) throw new Error('Reset failed');
                    const body = await res.json();
                    if (body.no_recommendations) {
                      // eslint-disable-next-line no-alert
                      window.alert(
                        'No recommended regulations were found for the hazards in this RA — they appear to reference pre-v1.2 library entries that don’t carry regs.\n\nYour existing selections were left unchanged. Add hazards from the current library (or pick chips manually) to populate the list.'
                      );
                    }
                    setRa((prev) => ({ ...prev, regs_list: body.regs_list }));
                  } catch (err) {
                    console.error('[Detail] regs reset failed:', err);
                  }
                  setBusyAction(false);
                }}
                className={`${BTN_XS.secondary} shrink-0`}
                disabled={busyAction}
                title="Rebuild the list from each hazard's recommended regs"
              >
                Reset to auto
              </button>
            )}
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        id={`ra-${ra.id}-pre-review`}
        title="Pre-Assessment Review"
        tint="amber"
        number={2}
        defaultCollapsed={!ra.pre_review_answers || Object.keys(ra.pre_review_answers).length === 0}
        headerExtra={areAnswersComplete(PRE_REVIEW_QUESTIONS, ra.pre_review_answers) ? <CompletedTick /> : null}
      >
        <RaReviewSection
          questions={PRE_REVIEW_QUESTIONS}
          answers={ra.pre_review_answers || {}}
          canEdit={canEdit}
          onChange={(next) => setRa((prev) => ({ ...prev, pre_review_answers: next }))}
          onCommit={(next) => updateHeaderField('pre_review_answers', next)}
          testIdPrefix="pre"
        />
      </CollapsibleCard>

      <CollapsibleCard
        id={`ra-${ra.id}-hazards`}
        title="Hazards"
        tint="slate"
        number={3}
        defaultCollapsed={(ra.sections || []).length === 0}
        headerExtra={
          <>
            <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
              ({(ra.sections || []).length})
            </span>
            {(ra.sections || []).length > 0 && <CompletedTick />}
          </>
        }
      >
        {ra.sections.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">No hazards yet.</p>
        )}
        <div className="space-y-4">
          {ra.sections.map((section) => (
            <SectionPanel
              key={section.id}
              section={section}
              canEdit={canEdit}
              busy={busyAction}
              canSaveToWorkspace={canSaveToWorkspace && !!myPersonalProductionId}
              canSaveToDept={canSaveToDept}
              whoAffectedSuggestions={whoAffectedSuggestions}
              onSaveRow={(rowId, patch) => updateRow(rowId, patch, section.id)}
              onMarkRowDirty={(rowId) => markRowDirty(rowId)}
              onApplyFromLibrary={(rowId, block) => applyFromLibrary(section.id, rowId, block)}
              onSaveAsLibraryBlock={(rowId, targets, category) => saveAsLibraryBlock(section.id, rowId, targets, category)}
              onRemoveSection={() => removeSection(section.id)}
              onPullLatest={() => pullLatest(section.id)}
            />
          ))}
        </div>
        {canEdit && (
          <button
            onClick={addBlankSection}
            className="mt-3 w-full py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/30"
            disabled={busyAction}
            data-testid="ra-detail-add-section"
          >
            + Add hazard
          </button>
        )}
      </CollapsibleCard>

      <CollapsibleCard
        id={`ra-${ra.id}-post-review`}
        title="Residual Risk Review"
        tint="emerald"
        number={4}
        defaultCollapsed={!ra.post_review_answers || Object.keys(ra.post_review_answers).length === 0}
        headerExtra={areAnswersComplete(POST_REVIEW_QUESTIONS, ra.post_review_answers) ? <CompletedTick /> : null}
      >
        <RaReviewSection
          questions={POST_REVIEW_QUESTIONS}
          answers={ra.post_review_answers || {}}
          canEdit={canEdit}
          onChange={(next) => setRa((prev) => ({ ...prev, post_review_answers: next }))}
          onCommit={(next) => updateHeaderField('post_review_answers', next)}
          testIdPrefix="post"
        />
        {isAuthor && isDraft && (
          <button
            type="button"
            onClick={() => setShowSignOff(true)}
            className={`${BTN.confirm} mt-4 w-full justify-center`}
            disabled={busyAction}
            data-testid="ra-detail-signoff-bottom"
          >
            Save / Sign off
          </button>
        )}
      </CollapsibleCard>

      {ra.signatures.length > 0 && (
        <CollapsibleCard id={`ra-${ra.id}-signatures`} title="Signatures" tint="indigo" number={5}>
          <ul className="space-y-1 text-sm">
            {ra.signatures.map((s) => (
              <li key={s.id} className="flex items-center gap-3 text-gray-700 dark:text-gray-200">
                <span className="font-medium">{s.signer_name}</span>
                {s.signer_role && <span className="text-xs text-gray-500 dark:text-gray-400">· {s.signer_role}</span>}
                <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{formatDateTime(s.signed_at)}</span>
              </li>
            ))}
          </ul>
        </CollapsibleCard>
      )}

      {showSignOff && (
        <RiskAssessmentSignOffModal
          raId={ra.id}
          isFirstSignature={ra.signatures.length === 0}
          isDirty={isDirty}
          onSave={handleSaveAll}
          onClose={() => setShowSignOff(false)}
          onSigned={handleSigned}
          canSaveToWorkspace={canSaveToWorkspace}
          canSaveToDept={canSaveToDept}
          myPersonalProductionId={myPersonalProductionId}
          defaultTemplateName={`${ra.title} (template)`}
          onSaveAsTemplate={saveAsTemplate}
        />
      )}

      <OverallRiskFooter stats={overallStats} pushUp={canEdit} />

      {/* Explicit-Save sticky action bar — only when canEdit. Sits below the
          OverallRiskFooter (which is pushed up by `pushUp`). */}
      {canEdit && (
        <div
          className="fixed bottom-0 left-0 right-0 sm:left-64 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-2.5 z-30"
          data-testid="ra-detail-save-bar"
        >
          <div className="max-w-full mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs">
              {isDirty ? (
                <span className="text-amber-600 dark:text-amber-400 font-medium">
                  ● Unsaved changes
                </span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">All saved</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isDirty && (
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={busyAction}
                  className={BTN.secondary}
                  data-testid="ra-detail-cancel"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={handleSaveAll}
                disabled={!isDirty || busyAction}
                className={BTN.confirm}
                data-testid="ra-detail-save"
              >
                {busyAction ? 'Saving…' : 'Quick save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function SaveIndicator({ state, error }) {
  if (state === 'idle')   return <span className="text-xs text-gray-400 dark:text-gray-500">Auto-saves on edit</span>;
  if (state === 'saving') return <span className="text-xs text-amber-600 dark:text-amber-400">Saving…</span>;
  if (state === 'saved')  return <span className="text-xs text-green-600 dark:text-green-400">✓ Saved</span>;
  return (
    <span
      className="text-xs text-red-600 dark:text-red-400"
      title={error || 'Save failed'}
    >
      ✕ Save failed — retry the last edit
    </span>
  );
}

// Numbered circle badge — visually anchors each step in the journey.
function StepBadge({ n, tone = 'gray' }) {
  const toneClass = tone === 'indigo'
    ? 'bg-indigo-600 text-white dark:bg-indigo-500'
    : 'bg-gray-400 text-white dark:bg-gray-500';
  return (
    <span
      className={`inline-flex items-center justify-center h-5 w-5 rounded-full text-xs font-bold shrink-0 ${toneClass}`}
      aria-hidden
    >
      {n}
    </span>
  );
}

// Arrow between journey steps. Horizontal on lg+ (between columns),
// vertical on smaller screens (between stacked rows). Decorative only.
function FlowArrow() {
  return (
    <div
      className="flex items-center justify-center text-gray-400 dark:text-gray-500 select-none py-1 lg:py-0"
      aria-hidden
    >
      {/* Horizontal arrow (lg and up) */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="hidden lg:block h-6 w-6"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7-7 7M3 12h18" />
      </svg>
      {/* Vertical arrow (mobile) */}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="lg:hidden h-6 w-6"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    </div>
  );
}

function HeaderTitle({ ra, canEdit, onSave, onDirty }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(ra.title || '');
  useEffect(() => { setDraft(ra.title || ''); }, [ra.title]);

  if (!editing || !canEdit) {
    return (
      <span
        data-testid="ra-detail-title-display"
        onClick={() => canEdit && setEditing(true)}
        className={canEdit ? 'cursor-text hover:underline' : ''}
      >
        {ra.title}
      </span>
    );
  }
  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft !== ra.title) onSave(draft.trim());
    else setDraft(ra.title || '');
  };
  const handleChange = (e) => {
    const next = e.target.value;
    setDraft(next);
    // Signal dirty as soon as the draft diverges so the save-bar pill flips
    // immediately, without committing (that still happens on blur — keeps
    // Escape-to-cancel working).
    if (onDirty && next !== (ra.title || '')) onDirty();
  };
  return (
    <input
      autoFocus
      type="text"
      data-testid="ra-detail-title-input"
      value={draft}
      onChange={handleChange}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(ra.title || ''); setEditing(false); } }}
      className="text-xl font-semibold rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-0.5"
    />
  );
}

function HeaderField({ label, value, canEdit, onSave, onDirty, type = 'text', multiline = false, wide = false }) {
  const [draft, setDraft] = useState(value || '');
  useEffect(() => { setDraft(value || ''); }, [value]);

  const commit = () => {
    if ((draft || '') !== (value || '')) onSave(draft || '');
  };
  const handleChange = (e) => {
    const next = e.target.value;
    setDraft(next);
    if (onDirty && (next || '') !== (value || '')) onDirty();
  };

  const inputClass = 'block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-70';

  return (
    <label className={`${multiline ? 'flex flex-col' : 'block'} ${wide ? 'sm:col-span-2' : ''}`}>
      <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      {multiline ? (
        <textarea
          value={draft}
          onChange={handleChange}
          onBlur={commit}
          rows={8}
          disabled={!canEdit}
          className={`${inputClass} flex-1 resize-y min-h-[10rem]`}
        />
      ) : (
        <input
          type={type}
          value={draft || ''}
          onChange={handleChange}
          onBlur={commit}
          disabled={!canEdit}
          className={inputClass}
        />
      )}
    </label>
  );
}

function SectionPanel({ section, canEdit, busy, canSaveToWorkspace, canSaveToDept, whoAffectedSuggestions, onSaveRow, onMarkRowDirty, onApplyFromLibrary, onSaveAsLibraryBlock, onRemoveSection, onPullLatest }) {
  // v1.1: atomic sections — always exactly one row. Migration enforces this
  // server-side; the UI also avoids ever creating a 0- or 2+-row section.
  const row = section.rows[0];
  const initHrn = row ? computeHrn(row.lo_init, row.fe_init, row.dph_init, row.np_init) : 0;
  const resHrn  = row ? computeHrn(row.lo_res,  row.fe_res,  row.dph_res,  row.np_res)  : 0;
  // Collapse state. Default OPEN for empty hazards (so the user can edit
  // right away) and CLOSED for hazards that already have content (saves
  // vertical space on RAs with many filled hazards).
  const hasContent = !!(row && row.hazard && row.hazard.trim());
  const [collapsed, setCollapsed] = useState(hasContent);
  const hazardTitle = (row && row.hazard) || section.name || 'New hazard';
  // When the title is short enough to leave room for the risk bar beside it,
  // we promote the bar inline on desktop (same row as the title). Threshold
  // chosen so "Gimbal / motion platform failure causing fall or crush causing
  // fall or crush" (76 chars) still fits inline; anything longer drops the bar
  // back below the title to avoid wrapping. Mobile always uses the below-title
  // position regardless of title length.
  const isLongTitle = hazardTitle.length > 76;

  return (
    <div
      /* 📱 Below `md` (mobile batch item 14) the hazard card and its detail
         panel give back their padding (p-4 → p-2, p-3 → px-1.5): five nested
         boxes left the fields ~250px of a 375px phone. The header's negative
         margins track the card's padding. Desktop untouched. */
      className={`bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-md:p-2 transition-all ${collapsed ? 'cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700/60' : ''}`}
      onClick={collapsed ? () => setCollapsed(false) : undefined}
    >
      <div
        className={`group flex items-center gap-3 flex-wrap cursor-pointer transition-colors ${collapsed ? '' : '-mx-4 -mt-4 px-4 pt-4 pb-3 mb-3 max-md:-mx-2 max-md:-mt-2 max-md:px-2 max-md:pt-2 rounded-t-lg bg-slate-50 dark:bg-slate-700/30 hover:bg-slate-100 dark:hover:bg-slate-700/50'}`}
        onClick={() => setCollapsed((c) => !c)}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setCollapsed((c) => !c);
          }
        }}
        data-testid={`ra-detail-section-toggle-${section.id}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0 transition-transform group-hover:text-gray-700 dark:group-hover:text-gray-200 ${collapsed ? '-rotate-90' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
        <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
          {hazardTitle}
        </h3>
        {/* Inline risk bar when collapsed — saves a row of vertical space.
            Fills the gap between title and right-aligned actions. The negative
            top margin lifts the bar strip so it visually centers on the title
            text (the bar has marker labels above the strip pushing it down). */}
        {collapsed && row && (
          <div className="flex-1 min-w-[160px] -mt-3">
            <RiskReductionBar
              initialHrn={initHrn}
              residualHrn={resHrn}
              disabled={!canEdit}
            />
          </div>
        )}
        {/* Inline risk bar when expanded — desktop only, short titles only.
            Mobile and long-title cases fall back to the below-title position. */}
        {!collapsed && !isLongTitle && row && (
          <div className="hidden lg:block flex-1 min-w-[160px] -mt-3">
            <RiskReductionBar
              initialHrn={initHrn}
              residualHrn={resHrn}
              disabled={!canEdit}
            />
          </div>
        )}
        <div className="flex items-center gap-2 ml-auto shrink-0">
          {section.updates_available && (
            <span
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
              title="The source hazard has changed since this section was added."
            >
              ↑ Update available
            </span>
          )}
          {section.updates_available && canEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPullLatest(); }}
              className="text-[11px] px-2 py-0.5 rounded border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 disabled:opacity-50"
              disabled={busy}
              title="Refresh untouched fields from the current library hazard. Your customisations are preserved."
              data-testid={`ra-detail-pull-latest-${section.id}`}
            >
              Pull latest
            </button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onRemoveSection(); }}
              className={BTN_XS['danger-soft']}
              disabled={busy}
              data-testid={`ra-detail-section-remove-${section.id}`}
            >
              Remove hazard
            </button>
          )}
        </div>
      </div>

      {/* Below-title risk bar — shown when expanded on mobile, and on desktop
          only when the title is too long to fit the bar inline. Collapsed
          state always renders the bar inline in the header above. */}
      {row && !collapsed && (
        <div className={`mb-4 ${!isLongTitle ? 'lg:hidden' : ''}`}>
          <RiskReductionBar
            initialHrn={initHrn}
            residualHrn={resHrn}
            disabled={!canEdit}
          />
        </div>
      )}

      {/* Full editor only when expanded */}
      {row && !collapsed && (
        <RowEditor
          key={row.id}
          row={row}
          canEdit={canEdit}
          busy={busy}
          canSaveToWorkspace={canSaveToWorkspace}
          canSaveToDept={canSaveToDept}
          whoAffectedSuggestions={whoAffectedSuggestions}
          onSave={(patch) => onSaveRow(row.id, patch)}
          onMarkDirty={() => onMarkRowDirty && onMarkRowDirty(row.id)}
          onApplyFromLibrary={(block) => onApplyFromLibrary(row.id, block)}
          onSaveAsLibraryBlock={(targets, category) => onSaveAsLibraryBlock(row.id, targets, category)}
        />
      )}
    </div>
  );
}

function RowEditor({ row, canEdit, busy, canSaveToWorkspace, canSaveToDept, whoAffectedSuggestions = [], onSave, onMarkDirty, onApplyFromLibrary, onSaveAsLibraryBlock }) {
  const [hazard, setHazard] = useState(row.hazard || '');
  const [who, setWho] = useState(row.who_affected || '');
  const [controls, setControls] = useState(row.controls || '');
  const [notes, setNotes] = useState(row.notes || '');
  useEffect(() => { setHazard(row.hazard || ''); }, [row.hazard]);
  useEffect(() => { setWho(row.who_affected || ''); }, [row.who_affected]);
  useEffect(() => { setControls(row.controls || ''); }, [row.controls]);
  useEffect(() => { setNotes(row.notes || ''); }, [row.notes]);

  // Hide the Initial + Residual HRN scoring grids until a hazard is named.
  // Otherwise the 8 factor dropdowns paint at the same time as the empty
  // hazard input and pull the user's attention before they've decided what
  // they're scoring.
  const hasHazard = !!(hazard && hazard.trim());

  const inputClass = 'block w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 px-2 py-1 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-70';

  return (
    <div className="rounded-md bg-gray-50 dark:bg-gray-700/30 p-3 max-md:px-1.5" data-testid={`ra-detail-row-${row.id}`}>
      <div className="grid gap-2 sm:grid-cols-2 mb-2">
        <label className="block">
          <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Hazard</span>
          <HazardAutocomplete
            value={hazard}
            onChange={(val) => {
              setHazard(val);
              // Flip the save-bar pill on keystroke; commit still waits for blur.
              if (onMarkDirty && val !== (row.hazard || '')) onMarkDirty();
            }}
            onBlur={(val) => {
              // Persist plain-text edits when the input loses focus.
              if (val !== row.hazard) onSave({ hazard: val });
            }}
            onSelect={(block) => {
              // Library pick — page-level callback fetches the block detail
              // and PUTs the full row payload. Local state will sync from the
              // optimistic setRa update.
              onApplyFromLibrary(block);
            }}
            onSaveAsBlock={(val, targets, category) => {
              // Persist any pending hazard text first so the library block
              // captures the latest field values.
              if (val !== row.hazard) onSave({ hazard: val });
              onSaveAsLibraryBlock(targets, category);
            }}
            canSaveToWorkspace={canSaveToWorkspace}
            canSaveToDept={canSaveToDept}
            disabled={!canEdit || busy}
            placeholder="Type a hazard or pick from library…"
          />
        </label>
        <label className="block">
          <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Who is affected</span>
          <input
            type="text"
            value={who}
            onChange={(e) => {
              const v = e.target.value;
              setWho(v);
              if (onMarkDirty && v !== (row.who_affected || '')) onMarkDirty();
            }}
            onBlur={() => { if (who !== row.who_affected) onSave({ who_affected: who }); }}
            disabled={!canEdit}
            list={`who-affected-suggestions-${row.id}`}
            className={inputClass}
            placeholder="e.g. Operator, crew nearby, public"
          />
          {whoAffectedSuggestions.length > 0 && (
            <datalist id={`who-affected-suggestions-${row.id}`}>
              {whoAffectedSuggestions.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          )}
        </label>
      </div>

      {/* Initial → Controls → Residual: a 3-step journey. Arrows between
          panels on lg+ make the flow obvious; on smaller widths the
          natural vertical stack does the same job.
          The whole 3-step grid (Initial / Controls / Residual) is hidden
          until a hazard is named — naming the hazard is the prerequisite
          for everything that follows, so we don't surface scoring or
          control-writing UI until the user has decided what they're
          assessing. */}
      {hasHazard && (
      <div className="relative grid gap-3 lg:grid-cols-[2fr_auto_4fr_auto_2fr] lg:items-stretch">
        {/* Step 1 — INITIAL */}
        <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-700/20 p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <StepBadge n={1} tone="gray" />
            <div>
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 leading-tight">Initial risk</div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 leading-tight">
                Before controls
              </div>
            </div>
          </div>
          <HrnInput
            values={{ lo: row.lo_init, fe: row.fe_init, dph: row.dph_init, np: row.np_init }}
            onChange={(next) => onSave({
              lo_init: next.lo, fe_init: next.fe, dph_init: next.dph, np_init: next.np,
            })}
            disabled={!canEdit}
            testIdPrefix={`ra-detail-row-${row.id}-init`}
          />
        </div>

        {/* Arrow 1 → 2 */}
        <FlowArrow />

        {/* Step 2 — CONTROLS. flex-col + textarea flex-1 = the textarea fills
            the full panel height, matching the Initial/Residual panels next
            to it (the grid uses `lg:items-stretch` so panels are equal height). */}
        <label className="rounded-md border border-indigo-200 dark:border-indigo-800/60 bg-indigo-50/40 dark:bg-indigo-900/10 p-2.5 flex flex-col">
          <div className="flex items-center gap-1.5 mb-1">
            <StepBadge n={2} tone="indigo" />
            <div>
              <div className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 leading-tight">Control measures</div>
              <div className="text-[10px] uppercase tracking-wider text-indigo-700/70 dark:text-indigo-300/70 leading-tight">
                What you do to reduce risk
              </div>
            </div>
          </div>
          <div className="flex-1 min-h-[140px] max-h-[360px] overflow-y-auto pr-1">
            <ControlsByStage
              value={controls}
              onChange={(next) => {
                setControls(next);
                if (onMarkDirty && next !== (row.controls || '')) onMarkDirty();
              }}
              onCommit={(next) => { if (next !== row.controls) onSave({ controls: next }); }}
              disabled={!canEdit}
              compact
            />
          </div>
        </label>

        {/* Arrow 2 → 3 */}
        <FlowArrow />

        {/* Step 3 — RESIDUAL */}
        <div className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-700/20 p-2.5">
          <div className="flex items-center gap-1.5 mb-1">
            <StepBadge n={3} tone="gray" />
            <div>
              <div className="text-sm font-semibold text-gray-700 dark:text-gray-200 leading-tight">Residual risk</div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 leading-tight">
                After controls
              </div>
            </div>
          </div>
          <HrnInput
            values={{ lo: row.lo_res, fe: row.fe_res, dph: row.dph_res, np: row.np_res }}
            comparedTo={computeHrn(row.lo_init, row.fe_init, row.dph_init, row.np_init)}
            onChange={(next) => onSave({
              lo_res: next.lo, fe_res: next.fe, dph_res: next.dph, np_res: next.np,
            })}
            disabled={!canEdit}
            testIdPrefix={`ra-detail-row-${row.id}-res`}
          />
        </div>
      </div>
      )}

      <label className="block mt-3">
        <span className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Notes</span>
        <input
          type="text"
          value={notes}
          onChange={(e) => {
            const v = e.target.value;
            setNotes(v);
            if (onMarkDirty && v !== (row.notes || '')) onMarkDirty();
          }}
          onBlur={() => { if (notes !== row.notes) onSave({ notes }); }}
          disabled={!canEdit}
          className={inputClass}
        />
      </label>
    </div>
  );
}
